import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faCalendarClock,
	faCircleDollar,
	faGift,
	faPartyHorn,
	faSquareCheck,
	faSquareInfo,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ApiService, useAnnouncementsStore } from '@supply-mover-2-0/core';
import React, { useEffect, useState } from 'react';

import { useTranslation } from '../providers/TranslationProvider';

interface AlertBannerEntity {
	id: number;
	name: string;
	title: string;
	color: string;
	icon: string;
	startDate: Date | null;
	endDate: Date | null;
	isDismissable: boolean;
	isPublic: boolean;
}

const iconMap: Record<string, IconDefinition | null> = {
	'': null,
	'fa-square-info': faSquareInfo,
	'fa-square-check': faSquareCheck,
	'fa-party-horn': faPartyHorn,
	'fa-gift': faGift,
	'fa-calendar-clock': faCalendarClock,
	'fa-circle-dollar': faCircleDollar,
};

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const ActiveAnnouncementsBanner: React.FC<{
	location: 'crm' | 'ecommerce';
	/**
	 * How to load the announcements. The CRM leaves this unset and the
	 * component calls the CRM API itself. The storefront must supply one
	 * (PD-14790): it authenticates with a portal token against a different
	 * base URL, and its shoppers may be anonymous, so it cannot reach the CRM
	 * route at all. Injecting the fetch keeps ONE renderer, so a banner looks
	 * the same in both places and the colour palette lives in one file.
	 */
	fetchAnnouncements?: () => Promise<Array<AlertBannerEntity>>;
}> = ({ location, fetchAnnouncements }) => {
	const { t } = useTranslation();
	const [announcements, setAnnouncements] = useState<Array<AlertBannerEntity>>(
		[],
	);
	const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());

	useEffect(() => {
		// Load dismissed IDs from store
		const storedDismissedIds = useAnnouncementsStore.getState().dismissedIds;
		setDismissedIds(new Set(storedDismissedIds));
	}, []);

	useEffect(() => {
		const loadAnnouncements = async (): Promise<void> => {
			const locationParam = location === 'ecommerce' ? 'ecommerce' : 'crm';

			// Check cache from store
			const cached = useAnnouncementsStore
				.getState()
				.getCachedAnnouncements(locationParam, CACHE_TTL);

			if (cached != null) {
				setAnnouncements(cached as Array<AlertBannerEntity>);

				return;
			}

			try {
				const response =
					fetchAnnouncements != null
						? await fetchAnnouncements()
						: await ApiService.apiGet<Array<AlertBannerEntity>>(
								`/announcements/active/${locationParam}`,
							);

				// Save to store cache
				useAnnouncementsStore
					.getState()
					.setCachedAnnouncements(response, locationParam);
				setAnnouncements(response);
			} catch (error) {
				// A failure here renders nothing, which is indistinguishable from
				// "no announcements" — log it so a broken wiring is visible.
				console.debug('Failed to load announcements', error);
			}
		};

		void loadAnnouncements();

		const interval = setInterval(() => {
			void loadAnnouncements();
		}, CACHE_TTL);

		return () => {
			clearInterval(interval);
		};
	}, [location, fetchAnnouncements]);

	const handleDismiss = (id: number) => {
		const newDismissed = new Set(dismissedIds);

		newDismissed.add(id);
		setDismissedIds(newDismissed);

		// Save to store
		useAnnouncementsStore.getState().dismissAnnouncement(id);
	};

	const getIconDefinition = (iconName: string): IconDefinition | null => {
		if (!iconName || iconName === '') {
			return null;
		}
		return iconMap[iconName] ?? iconMap['fa-square-info'] ?? null;
	};

	const getColorStyles = (colorName: string) => {
		switch (colorName) {
			case 'grey':
				return {
					bg: '#f3f4f6',
					text: '#374151',
					icon: '#6b7280',
					border: '#9ca3af',
				};
			case 'brown':
				return {
					bg: '#fef3c7',
					text: '#78350f',
					icon: '#92400e',
					border: '#92400e',
				};
			case 'orange':
				return {
					bg: '#fed7aa',
					text: '#9a3412',
					icon: '#c2410c',
					border: '#ea580c',
				};
			case 'yellow':
				return {
					bg: '#fef08a',
					text: '#713f12',
					icon: '#a16207',
					border: '#eab308',
				};
			case 'green':
				return {
					bg: '#bbf7d0',
					text: '#14532d',
					icon: '#15803d',
					border: '#16a34a',
				};
			case 'light-green':
				return {
					bg: '#d9f99d',
					text: '#365314',
					icon: '#4d7c0f',
					border: '#84cc16',
				};
			case 'light-blue':
				return {
					bg: '#bfdbfe',
					text: '#1e3a8a',
					icon: '#2563eb',
					border: '#3b82f6',
				};
			case 'dark-blue':
				return {
					bg: '#93c5fd',
					text: '#1e3a8a',
					icon: '#1e40af',
					border: '#1d4ed8',
				};
			case 'purple':
				return {
					bg: '#e9d5ff',
					text: '#581c87',
					icon: '#7c3aed',
					border: '#9333ea',
				};
			case 'dark-pink':
				return {
					bg: '#fbcfe8',
					text: '#831843',
					icon: '#be185d',
					border: '#db2777',
				};
			case 'light-pink':
				return {
					bg: '#fce7f3',
					text: '#9f1239',
					icon: '#db2777',
					border: '#ec4899',
				};
			case 'red':
				return {
					bg: '#fecaca',
					text: '#7f1d1d',
					icon: '#b91c1c',
					border: '#dc2626',
				};
			default:
				return {
					bg: '#bfdbfe',
					text: '#1e3a8a',
					icon: '#2563eb',
					border: '#3b82f6',
				};
		}
	};

	const visibleAnnouncements = announcements.filter(
		(announcement) => !dismissedIds.has(announcement.id),
	);

	if (visibleAnnouncements.length === 0) {
		return null;
	}

	return (
		<div className="w-full">
			{visibleAnnouncements.map((announcement) => {
				const colorStyles = getColorStyles(announcement.color);

				const iconDef = getIconDefinition(announcement.icon);

				return (
					<div
						key={announcement.id}
						className="flex items-center justify-between px-2 py-1 border-b"
						style={{
							backgroundColor: colorStyles.bg,
							borderBottomColor: colorStyles.border,
						}}
						role="alert"
					>
						<div className="flex-1 flex items-center justify-center gap-3">
							{iconDef != null && (
								<div className="flex items-center flex-shrink-0">
									<FontAwesomeIcon
										icon={iconDef}
										className="text-xs"
										style={{ color: colorStyles.icon }}
									/>
								</div>
							)}

							<div
								className="text-xs font-medium flex items-center"
								style={{ color: colorStyles.text }}
							>
								{t(announcement.title)}
							</div>
						</div>

						{announcement.isDismissable ? (
							<button
								type="button"
								onClick={() => {
									handleDismiss(announcement.id);
								}}
								className="flex items-center flex-shrink-0 p-1 rounded transition-opacity hover:opacity-70"
								style={{ color: colorStyles.icon }}
								aria-label={t('Dismiss')}
							>
								<FontAwesomeIcon icon={faXmark} className="text-xs" />
							</button>
						) : null}
					</div>
				);
			})}
		</div>
	);
};
